import { logger } from './logger';

interface ErrorReport {
  message: string;
  stack?: string;
  context?: Record<string, any>;
  timestamp: number;
  severity: 'error' | 'warning' | 'critical';
}

class MonitoringService {
  private static instance: MonitoringService;
  private errorBuffer: ErrorReport[] = [];
  private readonly bufferSize = 50;
  private readonly flushInterval = 60000; // 1 minute
  private readonly criticalErrors = new Set([
    'auth',
    'payment',
    'subscription',
    'database',
    'api'
  ]);

  private constructor() {
    setInterval(() => this.flush(), this.flushInterval);
    window.addEventListener('unload', () => this.flush());

    // Global error handler
    window.addEventListener('error', (event) => {
      this.reportError(event.error, { source: 'window.error' });
    });

    // Global promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.reportError(event.reason, { source: 'unhandledrejection' });
    });
  }

  public static getInstance(): MonitoringService {
    if (!MonitoringService.instance) {
      MonitoringService.instance = new MonitoringService();
    }
    return MonitoringService.instance;
  }

  public reportError(error: Error | any, context?: Record<string, any>) {
    const errorReport: ErrorReport = {
      message: error?.message || String(error),
      stack: error?.stack,
      context,
      timestamp: Date.now(),
      severity: this.getSeverity(error)
    };

    // Add to buffer
    this.errorBuffer.push(errorReport);
    if (this.errorBuffer.length >= this.bufferSize) {
      this.flush();
    }

    // Log critical errors immediately
    if (errorReport.severity === 'critical') {
      logger.error('Critical Error:', {
        error: errorReport.message,
        context: errorReport.context
      });
    }
  }

  private getSeverity(error: Error | any): ErrorReport['severity'] {
    const errorString = error?.message?.toLowerCase() || String(error).toLowerCase();
    
    // Check for critical errors
    if (this.criticalErrors.has(errorString.split(' ')[0]) ||
        errorString.includes('fatal') ||
        errorString.includes('critical')) {
      return 'critical';
    }

    // Check for warnings
    if (errorString.includes('warning') ||
        errorString.includes('retry') ||
        errorString.includes('timeout')) {
      return 'warning';
    }

    return 'error';
  }

  private async flush() {
    if (this.errorBuffer.length === 0) return;

    if (import.meta.env.PROD) {
      try {
        // Group errors by severity
        const groupedErrors = this.errorBuffer.reduce((acc, error) => {
          acc[error.severity] = acc[error.severity] || [];
          acc[error.severity].push(error);
          return acc;
        }, {} as Record<ErrorReport['severity'], ErrorReport[]>);

        // Log critical errors immediately
        if (groupedErrors.critical?.length) {
          logger.error('Critical Errors:', groupedErrors.critical);
        }

        // Clear buffer after successful send
        this.errorBuffer = [];
      } catch (error) {
        logger.error('Failed to send error reports:', error);
      }
    }
  }

  public trackPerformance(name: string, data?: Record<string, any>) {
    if (import.meta.env.PROD) {
      performance.mark(name + '_start');
      
      return () => {
        performance.mark(name + '_end');
        performance.measure(name, name + '_start', name + '_end');

        const measure = performance.getEntriesByName(name).pop();
        if (measure && measure.duration > 1000) { // Over 1 second
          logger.warn('Slow operation:', {
            name,
            duration: measure.duration,
            ...data
          });
        }
      };
    }
    return () => {};
  }
}

export const monitoring = MonitoringService.getInstance();

// Helper function for error reporting
export function reportError(error: Error | any, context?: Record<string, any>) {
  monitoring.reportError(error, context);
}

// Helper function for performance tracking
export function trackPerformance(name: string, data?: Record<string, any>) {
  return monitoring.trackPerformance(name, data);
}